import { useState } from 'react'
import {
    Stack,
    Heading,
    Text,
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    HStack,
    Input,
    useDisclosure,
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerOverlay
} from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlternativeContactsService } from '@/shared/api/rest/services/alternative-contacts.service'
import { useAlertService } from '@/shared/lib/useAlertService'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import {
    getFromLocalStorage,
    deleteFromLocalStorage
} from '@/shared/utils/localStorageHelper'
import { useMyProfileAsParent } from '@/modules/parent/hooks/useMyProfileAsParent'
import { emergencyContactSchema } from './form-schema'
import { resizeAndUploadImage } from '@/shared/utils/imageUploadUtility'

type DataArray = {
    value: string
    label: string
}

function extractValues(dataArray: DataArray[]) {
    return dataArray.map((item) => item.value)
}

export default function AlternativeContactPage() {
    const { isOpen, onOpen, onClose } = useDisclosure()
    const alertService = useAlertService()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { data: childrenData, loading } = useMyProfileAsParent()
    const queryClient = useQueryClient()
    const { mutateAsync: createAlternativeContact } = useMutation({
        mutationFn: AlternativeContactsService.create,
        onError: (error: Error) => {
            alertService.error(error.message, error.name)
            setIsSubmitting(false)
        },
        onSuccess: async () => {
            if (childrenData?._id) {
                await queryClient.invalidateQueries({
                    queryKey: ['alternative-contacts', childrenData._id]
                })
            }
            alertService.success('Alternative contact added successfully', 'Congrats')
            deleteFromLocalStorage('image-url')
            setIsSubmitting(false)
            onClose()
        }
    })

    const {
        register,
        handleSubmit,
        control,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(emergencyContactSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            phoneNumber: '',
            address: '',
            city: '',
            state: '',
            country: '',
            children: [],
            picture: ''
        }
    })

    const onSubmit = async (formValue: any) => {
        setIsSubmitting(true)

        /* cache image incase of error on backend */
        const imageUrl = getFromLocalStorage('image-url')
        const hasImageBeenUploaded = await resizeAndUploadImage(formValue, imageUrl)

        const values = {
            ...formValue,
            picture: hasImageBeenUploaded
        }

        try {
            await createAlternativeContact({
                firstName: values.firstName,
                lastName: values.lastName,
                phoneNumber: values.phoneNumber,
                address: values.address,
                city: values.city,
                state: values.state,
                country: values.country,
                picture: values.picture,
                children: extractValues(values.children)
            })
            deleteFromLocalStorage('image-url')
        } catch (error: any) {
            alertService.error(error.message, error.name)
        }
        setIsSubmitting(false)
    }

    const options: DataArray[] =
        childrenData &&
        childrenData.children.map((child: any) => ({
            value: child._id,
            label: `${child.firstName}${child.middleName ? ' ' + child.middleName : ''} ${child.lastName}`
        }))

    return (
        <>
            {/* // "Guardian" is being used to discourage parent from double entry
            //  given they can add spouse as "co-parent" */}
            <Button colorScheme="brand" size={'lg'} onClick={onOpen}>
                Add Guardian
            </Button>
            <Drawer isOpen={isOpen} placement="right" onClose={onClose} size={'md'}>
                <DrawerOverlay />
                <DrawerContent pt={16}>
                    <DrawerCloseButton />
                    <DrawerBody>
                        <Stack textAlign={'center'} mb="4">
                            <Heading fontSize={'2xl'}>Guardian Contact</Heading>
                            <Text fontSize={{ base: 'sm', sm: 'md' }} color={'gray.600'}>
                                Provide details of the person that can stand-in for you
                                when you are not available 🙅‍♀️
                            </Text>
                        </Stack>

                        <Stack spacing={4}>
                            <Stack spacing={4}>
                                <HStack>
                                    <FormControl
                                        isInvalid={Boolean(errors.firstName)}
                                        isRequired
                                    >
                                        <FormLabel>First Name</FormLabel>
                                        <Input
                                            type="text"
                                            id="firstName"
                                            {...register('firstName')}
                                        />
                                        <FormErrorMessage>
                                            {errors.firstName && errors.firstName.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={Boolean(errors.lastName)}
                                        isRequired
                                    >
                                        <FormLabel>Last Name</FormLabel>
                                        <Input
                                            type="text"
                                            id="lastName"
                                            {...register('lastName')}
                                        />
                                        <FormErrorMessage>
                                            {errors.lastName && errors.lastName.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </HStack>
                                <FormControl
                                    isInvalid={Boolean(errors.phoneNumber)}
                                    isRequired
                                >
                                    <FormLabel>Phone Number</FormLabel>
                                    <Input
                                        type="tel"
                                        id="phoneNumber"
                                        {...register('phoneNumber')}
                                    />

                                    <FormErrorMessage>
                                        {errors.phoneNumber && errors.phoneNumber.message}
                                    </FormErrorMessage>
                                </FormControl>
                                <HStack>
                                    <FormControl
                                        isInvalid={Boolean(errors.address)}
                                        isRequired
                                    >
                                        <FormLabel>Address</FormLabel>
                                        <Input
                                            type="text"
                                            id="address"
                                            {...register('address')}
                                        />
                                        <FormErrorMessage>
                                            {errors.address && errors.address.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={Boolean(errors.state)}
                                        isRequired
                                    >
                                        <FormLabel>State</FormLabel>
                                        <Input
                                            type="text"
                                            id="state"
                                            {...register('state')}
                                        />
                                        <FormErrorMessage>
                                            {errors.state && errors.state.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </HStack>
                                <HStack>
                                    <FormControl
                                        isInvalid={Boolean(errors.city)}
                                        isRequired
                                    >
                                        <FormLabel>City</FormLabel>
                                        <Input
                                            type="text"
                                            id="city"
                                            {...register('city')}
                                        />

                                        <FormErrorMessage>
                                            {errors.city && errors.city.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                    <FormControl
                                        isInvalid={Boolean(errors.country)}
                                        isRequired
                                    >
                                        <FormLabel>Country</FormLabel>
                                        <Input
                                            type="text"
                                            id="country"
                                            {...register('country')}
                                        />

                                        <FormErrorMessage>
                                            {errors.country && errors.country.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </HStack>
                                <FormControl
                                    isInvalid={Boolean(errors.picture)}
                                    isRequired
                                >
                                    <FormLabel>Person&apos;s Photo</FormLabel>
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        id="picture"
                                        {...register('picture')}
                                    />

                                    <FormErrorMessage>
                                        {errors.picture && errors.picture.message}
                                    </FormErrorMessage>
                                    <FormHelperText>
                                        Maximum image size allowed is 4MB 😉
                                    </FormHelperText>
                                </FormControl>
                                <FormControl
                                    isInvalid={Boolean(errors.children)}
                                    isRequired
                                >
                                    <FormLabel>Your Children</FormLabel>
                                    <Controller
                                        name="children"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                isMulti
                                                {...field}
                                                options={options}
                                                isLoading={loading}
                                            />
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {errors.children && errors.children.message}
                                    </FormErrorMessage>
                                    <FormHelperText>
                                        Children you want this person to checkin or
                                        checkout
                                    </FormHelperText>
                                </FormControl>
                                <Stack spacing={10} pt={2}>
                                    <Button
                                        type="submit"
                                        loadingText="Submitting"
                                        size="lg"
                                        isLoading={isSubmitting}
                                        onClick={handleSubmit(onSubmit)}
                                    >
                                        Submit
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </>
    )
}
